import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getCategoryName, getSubcategoryName } from '../utils/categoryNames';
import { toast } from 'sonner';
import { Trash, MagnifyingGlass, PencilSimple, Bell, BellSlash } from '@phosphor-icons/react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SavedSearchesPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const fetchSearches = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/saved-searches`, { withCredentials: true });
      setSearches(data);
    } catch {
      toast.error(t('savedSearches.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchSearches();
  }, [user, navigate, fetchSearches]);

  const handleDelete = async (searchId) => {
    try {
      await axios.delete(`${API}/saved-searches/${searchId}`, { withCredentials: true });
      setSearches(prev => prev.filter(s => s.search_id !== searchId));
      toast.success(t('savedSearches.deleted'));
    } catch {
      toast.error(t('savedSearches.deleteError'));
    }
  };

  const handleToggleNotify = async (searchId, currentNotify) => {
    try {
      await axios.put(`${API}/saved-searches/${searchId}`, { notify: !currentNotify }, { withCredentials: true });
      setSearches(prev => prev.map(s => s.search_id === searchId ? { ...s, notify: !currentNotify } : s));
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleSaveRename = async (searchId) => {
    if (!editName.trim()) return;
    try {
      await axios.put(`${API}/saved-searches/${searchId}`, { name: editName.trim() }, { withCredentials: true });
      setSearches(prev => prev.map(s => s.search_id === searchId ? { ...s, name: editName.trim() } : s));
      setEditingId(null);
      toast.success(t('savedSearches.updated'));
    } catch {
      toast.error('Failed to update');
    }
  };

  const runSearch = async (s) => {
    // Reset the "new listings" counter optimistically + server-side
    if (s.new_count > 0) {
      setSearches((prev) => prev.map((x) => x.search_id === s.search_id ? { ...x, new_count: 0 } : x));
      axios.post(`${API}/saved-searches/${s.search_id}/mark-viewed`, {}, { withCredentials: true }).catch(() => {});
    }
    const params = new URLSearchParams();
    if (s.search) params.set('search', s.search);
    if (s.category) params.set('category', s.category);
    if (s.subcategory) params.set('subcategory', s.subcategory);
    if (s.city) params.set('city', s.city);
    if (s.sort) params.set('sort', s.sort);
    navigate(`/browse?${params.toString()}`);
  };

  const buildTitle = (s) => {
    // Treat the stored name as a "custom rename" only if it differs from the raw slugs.
    // This keeps user renames but hides auto-generated internal labels like "vehicles".
    const catSlug = (s.category || '').toLowerCase();
    const subSlug = (s.subcategory || '').toLowerCase();
    const rawName = (s.name || '').trim();
    const normalizedName = rawName.toLowerCase();
    const isSlugName =
      !rawName ||
      normalizedName === catSlug ||
      normalizedName === subSlug ||
      normalizedName === `${catSlug}_${subSlug}` ||
      normalizedName === `${catSlug}-${subSlug}`;

    if (!isSlugName) return rawName;

    const catName = s.category ? getCategoryName(s.category, t) : null;
    const subName = s.subcategory ? getSubcategoryName(s.subcategory, t) : null;
    if (subName && catName) return `${subName} ${t('savedSearches.inside')} ${catName}`;
    if (catName) return catName;
    if (s.search) return `« ${s.search} »`;
    return t('savedSearches.allListings');
  };

  const buildSecondary = (s) => {
    // Secondary line holds the *additional* filters not already in the title.
    const catSlug = (s.category || '').toLowerCase();
    const subSlug = (s.subcategory || '').toLowerCase();
    const rawName = (s.name || '').trim().toLowerCase();
    const isSlugName =
      !rawName ||
      rawName === catSlug ||
      rawName === subSlug ||
      rawName === `${catSlug}_${subSlug}` ||
      rawName === `${catSlug}-${subSlug}`;

    const parts = [];
    // If the main title is the custom name, include category/subcategory context here.
    if (!isSlugName) {
      if (s.subcategory && s.category) {
        parts.push(`${getSubcategoryName(s.subcategory, t)} ${t('savedSearches.inside')} ${getCategoryName(s.category, t)}`);
      } else if (s.category) {
        parts.push(getCategoryName(s.category, t));
      }
    }
    // Keyword: skip if it's already the sole content of the title
    if (s.search && !(isSlugName && !s.category && !s.subcategory)) {
      parts.push(`« ${s.search} »`);
    }
    if (s.city) parts.push(s.city);
    return parts.join(' · ');
  };

  const handleDeleteAll = async () => {
    try {
      await axios.delete(`${API}/saved-searches`, { withCredentials: true });
      setSearches([]);
      toast.success(t('savedSearches.allDeleted'));
    } catch {
      toast.error(t('savedSearches.deleteError'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <Header />
        <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pb-20 md:pb-0">
      <Header />
      <div className="max-w-3xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-lg md:text-xl font-medium text-text_strong tracking-wide" data-testid="saved-searches-title">
            {t('savedSearches.title')}
          </h1>
          {searches.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs gap-1.5 text-danger border-danger hover:bg-danger-soft" data-testid="delete-all-searches-btn">
                  <Trash size={14} /> {t('savedSearches.deleteAll')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('savedSearches.deleteAllConfirmTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>{t('savedSearches.deleteAllConfirmDesc')}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteAll} className="bg-danger hover:bg-danger" data-testid="confirm-delete-all-btn">{t('common.delete')}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {searches.length === 0 ? (
          <div className="bg-white border border-border rounded-2xl p-8 text-center" data-testid="no-saved-searches">
            <MagnifyingGlass size={48} className="text-text_muted mx-auto mb-3" />
            <p className="text-text_muted mb-4">{t('savedSearches.empty')}</p>
            <Button onClick={() => navigate('/browse')} variant="primary">
              {t('nav.browse')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="saved-searches-list">
            {searches.map((s) => (
              <div key={s.search_id} className="bg-white border border-border rounded-xl p-4" data-testid={`saved-search-${s.search_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {editingId === s.search_id ? (
                      <div className="flex gap-2 items-center mb-2">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="border border-border text-sm h-8"
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveRename(s.search_id)}
                          autoFocus
                          data-testid="edit-name-input"
                        />
                        <Button size="sm" onClick={() => handleSaveRename(s.search_id)} className="bg-primary text-white h-8 px-3 text-xs" data-testid="save-name-btn">
                          {t('common.save')}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="h-8 px-3 text-xs">
                          {t('common.cancel')}
                        </Button>
                      </div>
                    ) : (
                      <button onClick={() => runSearch(s)} className="text-left group w-full">
                        <h3 className="font-semibold text-base text-text_main group-hover:text-primary transition-colors truncate" data-testid="search-name">
                          {buildTitle(s)}
                        </h3>
                      </button>
                    )}
                    {buildSecondary(s) && (
                      <p className="text-xs text-text_muted mt-0.5 truncate" data-testid="search-description">
                        {buildSecondary(s)}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="relative">
                      <button
                        onClick={() => handleToggleNotify(s.search_id, s.notify)}
                        className={`p-1.5 rounded-md transition-colors ${s.notify ? 'text-primary hover:bg-primary/10' : 'text-text_muted hover:bg-surface_muted'}`}
                        title={s.notify ? t('savedSearches.notifyOn') : t('savedSearches.notifyOff')}
                        data-testid="toggle-notify-btn"
                      >
                        {s.notify ? <Bell size={18} weight="fill" /> : <BellSlash size={18} />}
                      </button>
                      {s.new_count > 0 && (
                        <span
                          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-success text-white text-[10px] font-bold leading-none flex items-center justify-center border-2 border-white"
                          title={t('savedSearches.newCountTooltip', { count: s.new_count >= 10 ? 9 : s.new_count })}
                          data-testid="new-count-badge"
                        >
                          {s.new_count >= 10 ? '9+' : s.new_count}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => { setEditingId(s.search_id); setEditName(s.name); }}
                      className="p-1.5 rounded-md text-text_muted hover:bg-surface_muted transition-colors"
                      data-testid="edit-search-btn"
                    >
                      <PencilSimple size={18} />
                    </button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="p-1.5 rounded-md text-danger hover:bg-danger-soft transition-colors" data-testid="delete-search-btn">
                          <Trash size={18} />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('savedSearches.deleteConfirmTitle')}</AlertDialogTitle>
                          <AlertDialogDescription>{t('savedSearches.deleteConfirmDesc')}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(s.search_id)} className="bg-danger hover:bg-danger">{t('common.delete')}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <Button size="sm" variant="outline" onClick={() => runSearch(s)} className="h-8 px-3 text-xs border-primary text-primary hover:bg-primary hover:text-white" data-testid="run-search-btn">
                      <MagnifyingGlass size={14} className="mr-1" /> {t('common.search')}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
};
